import { startTransition } from 'react';
import axios from 'Adapter';
import {
	useQuery,
	UseQueryOptions,
	UseQueryResult,
	useMutation,
	UseMutationOptions,
	UseMutationResult
} from 'react-query';
import { queryKey } from 'Constants';
import { api, fetch, toastify } from 'Utils';
import {
	DataQueryParams,
	DataList,
	Job,
	JobFormData,
	Media,
	JobTagCategories,
	JobTags,
	ActivityTag,
	JobUsage,
	JobGenerateDescriptionByAIFormData,
	PublishJob,
	JobTop,
	JobSearchAnalytics
} from 'Types';
import { ca } from 'date-fns/locale';

const config = (options = {}) => {
	return {
		...options,
		keepPreviousData: false
	};
};

export const useSearchJobs = <T = PublishJob>(
	body: {
		pageSize: number;
		pageNumber: number;
		keyword: string;
		jobTags: string[];
		jobActivities: string[];
		locations: string[];
		topJob?: boolean;
		kind: number | null;
		postalCode: string;
		lon: number | null;
		lat: number | null;
		distance: number | null;
		businessSectors: string[];
		communityPartnerId?: string | null;
		categories?: string[];
	},
	options?: UseQueryOptions<T, any, PublishJob>
): UseQueryResult<PublishJob, unknown> => {
	return useQuery({
		queryKey: [queryKey.publishJobs, body],
		queryFn: () => fetch.publishJobs(body),
		...config({
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			...options
		})
	});
};

export const useProfileJobs = <T = DataList<Job>>(
	{
		status = '',
		pageSize = 12,
		pageNumber = 1,
		keyword = '',
		kind = ''
	}: DataQueryParams,
	options?: UseQueryOptions<T, any, DataList<Job>>
): UseQueryResult<DataList<Job>> => {
	return useQuery({
		queryKey: [
			queryKey.jobs,
			{ status, pageSize, pageNumber, keyword, kind }
		],
		queryFn: () =>
			fetch.profileJobList(status, pageSize, pageNumber, keyword, kind),
		...config(options)
	});
};

export const useJobTagCategories = <T = DataList<JobTagCategories>>(
	{ pageSize = 50, pageNumber = 1, keyword = '', kind = '' }: DataQueryParams,
	options?: UseQueryOptions<T, any, DataList<JobTagCategories>>
): UseQueryResult<DataList<JobTagCategories>> => {
	return useQuery({
		queryKey: [
			queryKey.jobTagCategories,
			{ pageSize, pageNumber, keyword, kind }
		],
		queryFn: () =>
			fetch.jobTagCategories(pageSize, pageNumber, keyword, kind),
		...config(options)
	});
};

export const useActivityTags = <T = DataList<ActivityTag>>(
	{
		pageSize = 50,
		pageNumber = 1,
		keyword = '',
		isActive = ''
	}: DataQueryParams,
	options?: UseQueryOptions<T, any, DataList<ActivityTag>>
): UseQueryResult<DataList<ActivityTag>> => {
	return useQuery({
		queryKey: [
			queryKey.activityTag,
			{ pageSize, pageNumber, keyword, isActive }
		],
		queryFn: () =>
			fetch.activityTags(pageSize, pageNumber, keyword, isActive),
		...config(options)
	});
};

export const useJobTags = <T = DataList<JobTags>>(
	{
		pageSize = 50,
		pageNumber = 1,
		keyword = '',
		kind = '',
		jobTagCategoryId = '',
		noAssignedCategory = '',
		isActive
	}: DataQueryParams,
	options?: UseQueryOptions<T, any, DataList<JobTags>>
): UseQueryResult<DataList<JobTags>> => {
	return useQuery({
		queryKey: [
			queryKey.jobTagCategories,
			{
				pageSize,
				pageNumber,
				kind,
				keyword,
				jobTagCategoryId,
				noAssignedCategory,
				isActive
			}
		],
		queryFn: () =>
			fetch.jobTags(
				pageSize,
				pageNumber,
				keyword,
				kind,
				jobTagCategoryId,
				noAssignedCategory,
				isActive
			),
		...config(options)
	});
};

/**
 * Fetches a list of jobs based on the provided query parameters.
 * @param jobId The ID of the job to fetch.
 * @param options Additional options for the query.
 * @returns The result of the query.
 */
export const useJob = <T = JobFormData>(
	jobId: string,
	options?: UseQueryOptions<T, any, JobFormData>
): UseQueryResult<JobFormData> => {
	return useQuery({
		queryKey: [queryKey.job, { id: jobId }],
		queryFn: () => fetch.jobDetails(jobId),
		...config(options)
	});
};

/**
 * Provides a function to submit a job creation request.
 * @returns An object with the submitRequest function.
 */
export const useCreateJob = () => {
	/**
	 * Submits a job creation request with the provided fields.
	 * @param fields The job form data.
	 * @param onFinal Optional callback to be called after the request is completed.
	 * @returns A Promise that resolves to the ID of the created job.
	 */
	const submitRequest: (
		fields: JobFormData,
		onFinal?: () => void
	) => Promise<string> = async (fields, onFinal) => {
		const data = fields;
		return await axios.post(api.createJob, data).then((res) => {
			if (res.status === 200) {
				startTransition(() => {
					onFinal?.();
				});
			}
			return res.data;
		});
	};

	return {
		submitRequest
	};
};

export const useUpdateJob = (job_id?: string) => {
	const query = useJob(job_id as string, {
		enabled: !!job_id,
		staleTime: Infinity,
		refetchOnMount: true
	});
	const job = query.data;

	// Update job with given job id
	const updateJob: (
		jobId: string,
		fields: Partial<JobFormData>,
		onFinal?: () => void
	) => Promise<void> = async (jobId, fields, onFinal) => {
		const data: Partial<JobFormData> = {
			...job,
			...fields
		};
		await axios.put(api.updateJob(jobId), data).then((res) => {
			if (res.status === 200) {
				query?.refetch();
				startTransition(() => {
					toastify('toastify.job-updated', {
						type: 'success'
					});
					onFinal?.();
				});
			}
		});
	};

	// Add Top job with job id
	const addTopJob: (
		jobId: string,
		fields: Partial<JobTop>,
		onFinal?: () => void
	) => Promise<void> = async (jobId, fields, onFinal) => {
		const data: Partial<JobTop> = {
			...fields
		};
		await axios.post(api.addTopJob(jobId), data).then((res) => {
			if (res.status === 200) {
				query?.refetch();
				startTransition(() => {
					toastify('toastify.job-updated', {
						type: 'success'
					});
					onFinal?.();
				});
			}
		});
	};

	// Publish job method
	const publishJob: (
		jobId: string,
		jobVisibilities?: any,
		onFinal?: () => void
	) => Promise<void> = async (jobId, jobVisibilities, onFinal) => {
		await axios
			.post(api.publishJob, { jobId, jobVisibility: jobVisibilities })
			.then((res) => {
				if (res.status === 200) {
					startTransition(() => {
						toastify('toastify.job-published', {
							type: 'success'
						});
						onFinal?.();
					});
				}
			});
	};

	// Unpublish job method
	const unPublishJob: (
		jobId: string,
		onFinal?: () => void
	) => Promise<void> = async (jobId, onFinal) => {
		await axios.post(api.unPublishJob, { jobId }).then((res) => {
			if (res.status === 200) {
				startTransition(() => {
					toastify('toastify.job-unPublished', {
						type: 'success'
					});
					onFinal?.();
				});
			}
		});
	};

	// Update and publish job simultaneously
	const saveAndPublishJob: (
		jobId: string,
		fields: JobFormData,
		onFinal?: () => void
	) => Promise<void> = async (jobId, fields, onFinal) => {
		const data: Partial<JobFormData> = {
			...job,
			...fields
		};
		await axios.put(api.updateJob(jobId), data).then(async (res) => {
			if (res.status === 200) {
				await publishJob(jobId, fields?.jobVisibilities, onFinal);
			}
		});
	};

	const updateJobFeature: (
		jobId: string,
		url: {
			featuredPhotoUrl?: string;
			featuredVideoUrl?: string;
		},
		onFinal?: () => void
	) => Promise<void> = async (jobId, url, onFinal) => {
		const data = {
			...(url.featuredPhotoUrl && {
				featuredPhotoUrl: url.featuredPhotoUrl
			}),
			...(url.featuredVideoUrl && {
				featuredVideoUrl: url.featuredVideoUrl
			})
		};
		await axios.patch(api.jobFeature(jobId), data).then(async (res) => {
			if (res.status === 200) {
				query?.refetch();
				startTransition(() => {
					onFinal?.();
				});
			}
		});
	};

	const updateContactImage: (
		jobId: string,
		fields: Partial<Media>,
		onFinal?: () => void
	) => Promise<void> = async (jobId, fields, onFinal) => {
		const data = {
			image: fields.url,
			thumbnailUrl: fields.thumbnailUrl
		};
		await axios.patch(api.updateContactImage(jobId), data).then((res) => {
			if (res.status === 200) {
				onFinal?.();
			}
		});
	};

	const removeJob: (
		jobId: string,
		onFinal: () => void
	) => Promise<void> = async (jobId, onFinal) => {
		await axios.delete(api.removeJob(jobId)).then((res) => {
			if (res.status === 200) {
				onFinal?.();
				startTransition(() => {
					toastify('toastify.job-removed', {
						type: 'success'
					});
				});
			}
		});
	};

	return {
		...query,
		updateJob,
		addTopJob,
		publishJob,
		unPublishJob,
		updateJobFeature,
		saveAndPublishJob,
		updateContactImage,
		removeJob,
		job
	};
};

export const useJobUsage = <T = JobUsage[]>(
	options?: UseQueryOptions<T, any, JobUsage[]>
): UseQueryResult<JobUsage[]> => {
	return useQuery({
		queryKey: [queryKey.jobUsage],
		queryFn: () => fetch.jobUsage(),
		...config(options)
	});
};

export const useGeneralJobUsage = <T = JobUsage[]>(
	options?: UseQueryOptions<T, any, JobUsage[]>
): UseQueryResult<JobUsage[]> => {
	return useQuery({
		queryKey: [queryKey.jobUsageGeneral],
		queryFn: () => fetch.jobGeneralUsage(),
		...config(options)
	});
};

export const useGenerateJobDescriptionByAI = () => {
	const submitRequest: (
		fields: JobGenerateDescriptionByAIFormData,
		onFinal?: (res) => void
	) => Promise<string> = async (fields, onFinal) => {
		const data = fields;
		return await axios
			.post(api.generateJobDescriptionByAI, data, {
				headers: {
					code: 's0zXFsu-E9elcd4h62Ees49DMygqI59FKf4HidoPQb-8AzFuHYLAFA%3D%3D'
				}
			})
			.then((res) => {
				if (res.status === 200) {
					startTransition(() => {
						onFinal?.(res.data);
					});
				}
				return res.data;
			});
	};

	return {
		submitRequest
	};
};
export const useJobInsight = (
	options?: UseMutationOptions<
		any,
		any,
		{ jobId: string; fromDate: string; toDate: string }
	>
): UseMutationResult<
	any,
	any,
	{ jobId: string; fromDate: string; toDate: string }
> => {
	return useMutation({
		mutationKey: [queryKey.jobInsight],
		mutationFn: (body: {
			jobId: string;
			fromDate: string;
			toDate: string;
		}) => fetch.jobInsight(body),
		...options
	});
};

//api/b2b/publishedjobs/search-with-analytics

export const useSearchWithAnalyticsJobs = <T = JobSearchAnalytics[]>(
	body: {
		jobStatus?: string;
		keyword: null | string;
		tenantId: string;
	},
	options?: UseQueryOptions<T, any, JobSearchAnalytics[]>
): UseQueryResult<JobSearchAnalytics[], unknown> => {
	return useQuery({
		queryKey: [queryKey.searchWithAnalyticsJobs, JSON.stringify(body)],
		queryFn: () => fetch.searchWithAnalyticsJobs(body),
		...config({
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			staleTime: 5 * 60 * 1000, // 5 minutes
			...options
		})
	});
};
